import { ErrorPage } from '@features/components/error-page';
import NoContentPage from '@features/components/no-content-page';
import { Spin } from '@m4b-design/components';
import cx from 'classnames';
import React from 'react';
import styled from 'styled-components';
import styles from './index.scss';

import {
  AttachmentDetail,
  Contents,
  KnowledgeDetail,
  LearningInfo,
  RoleType,
} from '@/api/seller/serv/oec_seller_learning_center_api';
import { LoginPanel } from '@/components';
import LabelRecommend from '@/components/label-recommend';
import { LabelTag } from '@/components/label-tag-uniform';
import CourseDisplay from '@/containers/course-detail/course-display';
import useCourseSeries, {
  ErrorText,
} from '@/containers/course-detail/index.hooks';
import Article from '@/containers/essay-detail/article';
import { register } from '@/libs';
import { changeQuery } from '@/libs/url';
import { PagePathV2 } from '@/types/index';
import ContentsTree from '@features/components/contents-tree';
import LoginWindow from '@features/components/login-window';
import { getQuery } from '@i18n-ecom/lib/dist/commonjs/url';
import { useHistory, useLocation } from 'react-router-dom';

const ErrorPageComponent = styled(ErrorPage)`
  position: relative;
  left: 0;
  right: 0;
`;

const CourseDetailPage: React.FC = () => {
  const {
    error,
    loading,
    empty,
    learningId,
    courseData,
    errorText,
    selectedKey,
    defaultOpenKeys,
    courseName,
    fetchCourseData,
    courseDetail,
    essayDetail,
    courseInfo,
    courseType,
    reportObj,
    needLogin,
  } = useCourseSeries();
  const history = useHistory();
  const location = useLocation();
  const articleType = getQuery(location.search, 'article_type')?.toString();
  const isNotAgreement = articleType !== 'agreement';
  return (
    <>
      <div className={styles.courseDetailLayout}>
        <div className={cx(styles.contentTree, 'h5:hidden')}>
          <div className={styles.leftTitle}>
            <div
              className={`text-16 text-neutral-text1 font-semibold mb-8 ${styles.courseName}`}
              title={courseInfo?.name}>
              {courseInfo?.name}
            </div>
            {!!courseInfo?.tag_info?.length && (
              <LabelTag tagInfo={courseInfo?.tag_info} />
            )}
          </div>
          <div className="-mt-4">
            {courseData?.course && defaultOpenKeys?.length && (
              <ContentsTree
                className={cx(styles.menuLayout, 'h5:hidden')}
                selectedKeys={[String(selectedKey)]}
                defaultOpenKeys={defaultOpenKeys}
                onClickMenuItem={key => {
                  const params = new URLSearchParams(
                    changeQuery({
                      learning_id: learningId || '',
                      content_id: key,
                    }),
                  );
                  params.delete('anchor_link');
                  history.push({
                    pathname: PagePathV2.CourseDetail,
                    search: params.toString(),
                  });
                }}
                contents={courseData.course as Contents[]}
                menuPage="seller_university_course_detail_page"
                menuModule="seller_university_course_detail"
              />
            )}
          </div>
        </div>
        <div className={`w-1 bg-neutral-line2 my-12`} />
        <div
          className={cx(
            styles.courseContentWrapper,
            error || loading || empty ? 'rounded-t-8' : '',
          )}>
          <Content
            errorText={errorText}
            courseType={courseType}
            courseDetail={courseDetail}
            contentId={selectedKey}
            courseName={courseName}
            essayDetail={essayDetail}
            courseInfo={courseInfo}
            needLogin={needLogin}
            loading={loading}
            error={error}
            empty={empty}
            reportObj={reportObj}
            fetchCourse={fetchCourseData}
          />
        </div>
      </div>
      {!loading && isNotAgreement && (
        <section className="pb-20">
          <LabelRecommend
            className={styles.courseRecommend}
            learningId={courseInfo?.learning_id}
          />
        </section>
      )}
    </>
  );
};

interface ContentProps {
  errorText: ErrorText;
  courseType: number;
  contentId?: number;
  courseDetail?: AttachmentDetail;
  essayDetail?: KnowledgeDetail;
  courseInfo?: LearningInfo;
  courseName?: string;
  loading?: boolean;
  error?: boolean;
  empty?: boolean;
  needLogin?: boolean;
  reportObj?: object;
  fetchCourse?: () => void;
}

const Content: React.FC<ContentProps> = props => {
  const {
    courseType,
    errorText,
    courseDetail,
    courseInfo,
    essayDetail,
    contentId,
    courseName,
    loading = false,
    error = false,
    empty = false,
    needLogin,
    reportObj,
  } = props;
  const { open: openLoginPanel } = LoginPanel.useModal();

  if (loading) {
    return (
      <div className="w-full h-full flex justify-center items-center rounded-8">
        <Spin />
      </div>
    );
  } else if (empty) {
    return (
      <div className="h-full mx-auto flex flex-1 justify-center items-center bg-white rounded-8">
        <NoContentPage />
      </div>
    );
  } else if (needLogin) {
    return (
      <div className="h-full mx-auto py-16 flex flex-1 justify-center items-center bg-white rounded-8">
        <LoginWindow
          onLogin={openLoginPanel}
          onRegister={() => register(RoleType.Seller)}
        />
      </div>
    );
  } else if (error) {
    return (
      <div className="h-full mx-auto py-16 flex flex-1 justify-center items-center bg-white rounded-8">
        <ErrorPageComponent
          hasNewVersion={true}
          title={errorText.title}
          text={errorText.text}
          leftButton={{
            type: 'primary',
            text: errorText.buttonText,
            onClick: errorText.buttonClick,
            style: {
              color: 'white',
              backgroundColor: '#1d1d1d',
            },
          }}
        />
      </div>
    );
  } else {
    return (
      <>
        {courseType === 2 && (
          <CourseDisplay
            className={styles.courseContent}
            name={courseName}
            courseDetail={courseDetail}
            courseInfo={courseInfo}
            contentId={contentId}
            reportObj={reportObj}
          />
        )}
        {courseType === 3 && (
          <Article
            className={styles.articleLayExtra}
            courseKnowledge={essayDetail}
            contentId={contentId}
            courseInfo={courseInfo}
            reportObj={reportObj}
          />
        )}
      </>
    );
  }
};

export default CourseDetailPage;
